import type { CharacterClassId } from './enums';

/**
 * The one place a character class's colour is defined.
 *
 * There were four, and they disagreed: `hud-html.ts` carried OKLCH values,
 * `character-select.ts` and `landing/main.ts` carried hex, and `global.css`
 * read a `--class-color` variable that was never declared in `tokens.css` —
 * so it silently used its own hardcoded fallback. Four sources, three of them
 * different, and nothing that would notice.
 *
 * Hex is canonical because three of the four already used it and because the
 * landing page renders these into inline `style` attributes with alpha
 * suffixes (`${color}44`), which OKLCH strings cannot do.
 *
 * `tokens.css` mirrors these as `--class-color-<id>` for stylesheet use;
 * `class-colors.test.ts` keeps the two in step.
 */
export const CLASS_COLORS: Record<CharacterClassId, string> = {
  soldier: '#4a7c59',
  medic: '#e05555',
  scout: '#5597cc',
  engineer: '#c89a3c',
  demolitions: '#cc5500',
};

/** Fallback for an unknown or missing class id. */
export const CLASS_COLOR_FALLBACK = CLASS_COLORS.soldier;

/**
 * Widened view for lookups by arbitrary string.
 *
 * `CLASS_COLORS` is total over `CharacterClassId`, so indexing it with a cast
 * would make the fallback below look unreachable to the type checker — while a
 * saved class id from an older localStorage entry can absolutely miss.
 */
const BY_ID: Record<string, string | undefined> = CLASS_COLORS;

/** Colour for a class id, falling back rather than rendering `undefined`. */
export function classColor(id: string | undefined): string {
  return (id === undefined ? undefined : BY_ID[id]) ?? CLASS_COLOR_FALLBACK;
}
